/* ============================================================
   PRENCO — Sistema de Cotización
   Convierte el flujo de compra en una solicitud de cotización
   por WhatsApp. Sin dependencias externas.

   - Intercepta los botones "añadir" del tema (T4S) en fase de
     captura, antes de que actúe el carrito nativo.
   - Persiste la cotización en localStorage.
   - Alimenta el contador del header, el drawer y la página
     "Mi cotización".

   Los datos de producto se leen de la API nativa de Shopify
   (/products/{handle}.js). Nunca se inventa información: si un
   producto no tiene SKU o precio, esos campos se omiten.
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.PrencoQuoteConfig || {};
  var STORAGE_KEY = 'prenco_quote_v1';
  var CONTACT_KEY = 'prenco_quote_contact_v1';

  var S = CFG.strings || {};
  var WHATSAPP = String(CFG.whatsapp || '573126611420').replace(/\D/g, '');

  /* Selectores de "añadir" del tema. */
  var TRIGGER_SELECTOR = '[data-atc-form], [data-action-atc], [data-atc-selector]';
  var FORM_SELECTOR = 'form[action*="/cart/add"], form[data-type="add-to-cart-form"]';

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function rootUrl() {
    var r = CFG.rootUrl || (window.T4Sroutes && window.T4Sroutes.root_url) || '/';
    return r.replace(/\/+$/, '') + '/';
  }

  /** Redimensiona una URL de la CDN de Shopify sin romper otras URLs. */
  function sizedImage(url, width) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url;
    var parts = url.split('?');
    var query = (parts[1] || '').split('&').filter(function (kv) {
      return kv && kv.indexOf('width=') !== 0;
    });
    query.push('width=' + width);
    return parts[0] + '?' + query.join('&');
  }

  /** Formateador de moneda compatible con los money_format de Shopify. */
  function formatMoney(cents) {
    var format = CFG.moneyFormat || '${{amount}}';
    var value = Number(cents);
    if (!isFinite(value)) return '';

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var name = match ? match[1] : 'amount';
    var formatted;

    switch (name) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        formatted = withDelimiters(value, 2, ',', '.');
    }
    return format.replace(placeholder, formatted);
  }

  /* ---------------------------------------------------------
     Almacén de la cotización
     --------------------------------------------------------- */
  var Store = {
    _items: null,

    read: function () {
      if (this._items) return this._items;
      var raw = null;
      try {
        raw = window.localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        /* localStorage no disponible: la cotización vive en memoria. */
      }
      var parsed = [];
      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          parsed = [];
        }
      }
      this._items = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      return this._items;
    },

    write: function (items) {
      this._items = items;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        /* Cuota agotada o modo privado: se mantiene en memoria. */
      }
      document.dispatchEvent(
        new CustomEvent('prenco:quote:updated', { detail: { items: items, count: Store.count() } })
      );
    },

    count: function () {
      return this.read().reduce(function (sum, item) {
        return sum + (parseInt(item.qty, 10) || 0);
      }, 0);
    },

    lines: function () {
      return this.read().length;
    },

    find: function (key) {
      var items = this.read();
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === key) return items[i];
      }
      return null;
    },

    add: function (item) {
      var items = this.read().slice();
      var existing = null;
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === item.key) {
          existing = items[i];
          break;
        }
      }
      if (existing) {
        existing.qty = (parseInt(existing.qty, 10) || 0) + (parseInt(item.qty, 10) || 1);
      } else {
        items.push(item);
      }
      this.write(items);
      return item;
    },

    setQty: function (key, qty) {
      qty = Math.max(1, Math.min(9999, parseInt(qty, 10) || 1));
      var items = this.read().slice();
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === key) items[i].qty = qty;
      }
      this.write(items);
    },

    remove: function (key) {
      this.write(
        this.read().filter(function (item) {
          return item.key !== key;
        })
      );
    },

    clear: function () {
      this.write([]);
    }
  };

  var Contact = {
    read: function () {
      try {
        return JSON.parse(window.localStorage.getItem(CONTACT_KEY)) || {};
      } catch (e) {
        return {};
      }
    },
    write: function (data) {
      try {
        window.localStorage.setItem(CONTACT_KEY, JSON.stringify(data));
      } catch (e) {
        /* silencioso */
      }
    }
  };

  /* ---------------------------------------------------------
     Resolución de datos de producto (API nativa de Shopify)
     --------------------------------------------------------- */
  var productCache = {};

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(rootUrl() + 'products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
    return productCache[handle];
  }

  /** Etiqueta legible de la variante ("Color: Verde Army"). */
  function variantLabel(product, variant) {
    if (!variant) return '';
    if (variant.title && variant.title !== 'Default Title') {
      var names = product && product.options ? product.options : null;
      var values = variant.options || [];
      if (names && names.length === values.length) {
        return names
          .map(function (name, i) {
            var label = typeof name === 'string' ? name : name && name.name;
            return label + ': ' + values[i];
          })
          .join(' / ');
      }
      return variant.title;
    }
    return '';
  }

  /**
   * Imagen real del producto. Algunos productos PRENCO sólo tienen modelo 3D,
   * por eso se recorre media[] y, en último lugar, la imagen ya visible en la página.
   */
  function productImage(product, variant, fallbackImage) {
    if (variant && variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    if (product && product.featured_image) return product.featured_image;
    if (product && product.media) {
      for (var i = 0; i < product.media.length; i++) {
        var m = product.media[i];
        if (m && m.preview && m.preview.image && m.preview.image.src) {
          return m.preview.image.src;
        }
        if (m && m.media_type === 'image' && m.src) return m.src;
      }
    }
    return fallbackImage || '';
  }

  /**
   * Lee la URL real de una imagen. El tema usa lazysizes, así que `src`
   * suele ser un placeholder base64 y la URL buena vive en `data-src`.
   */
  function realSrc(img) {
    if (!img) return '';
    var candidates = [
      img.getAttribute('data-src'),
      img.currentSrc,
      img.getAttribute('src'),
      img.getAttribute('data-bgset')
    ];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (c && c.indexOf('data:') !== 0) return c.split(' ')[0].trim();
    }
    return '';
  }

  /** Imagen visible en la página, como último recurso (dato real, no inventado). */
  function domImageNear(el) {
    var scope =
      el.closest('[data-product-featured]') ||
      el.closest('.t4s-product') ||
      el.closest('.shopify-section') ||
      document;
    var imgs = qsa('.t4s-product__media img, [data-main-media] img, img', scope);
    for (var i = 0; i < imgs.length; i++) {
      var src = realSrc(imgs[i]);
      if (src) return src;
    }
    var model = scope.querySelector('model-viewer[poster]');
    var poster = model ? model.getAttribute('poster') : '';
    return poster && poster.indexOf('data:') !== 0 ? poster : '';
  }

  /**
   * Construye la línea de cotización con datos reales.
   * Los campos ausentes (SKU, precio 0) se omiten deliberadamente.
   */
  function buildItem(product, variant, qty, properties, fallback) {
    fallback = fallback || {};
    var price = variant && typeof variant.price === 'number' ? variant.price : null;
    var image = productImage(product, variant, fallback.image);

    var propList = [];
    Object.keys(properties || {}).forEach(function (name) {
      var value = properties[name];
      if (value != null && String(value).trim() !== '') {
        propList.push({ name: name, value: String(value).trim() });
      }
    });

    var variantId = variant ? variant.id : fallback.variantId;
    var key = String(variantId);
    if (propList.length) {
      key +=
        ':' +
        propList
          .map(function (p) {
            return p.name + '=' + p.value;
          })
          .join('|');
    }

    return {
      key: key,
      variantId: variantId,
      productId: product ? product.id : fallback.productId || null,
      handle: product ? product.handle : fallback.handle || '',
      title: (product && product.title) || fallback.title || '',
      variantTitle:
        variant && variant.title && variant.title !== 'Default Title' ? variant.title : '',
      variantLabel: variantLabel(product, variant),
      sku: (variant && variant.sku) || '',
      image: sizedImage(image, 200),
      price: price && price > 0 ? price : null,
      url:
        product && product.handle
          ? rootUrl() + 'products/' + product.handle + (variant ? '?variant=' + variant.id : '')
          : fallback.url || '',
      properties: propList,
      qty: Math.max(1, parseInt(qty, 10) || 1)
    };
  }

  /* ---------------------------------------------------------
     Lectura del contexto del DOM
     --------------------------------------------------------- */
  function readCardOptions(el) {
    var card = el.closest('[data-product-options]');
    if (!card) return null;
    try {
      return JSON.parse(card.getAttribute('data-product-options'));
    } catch (e) {
      return null;
    }
  }

  function readFeaturedOptions(el) {
    var wrap = el.closest('[data-product-featured]');
    if (!wrap) return null;
    try {
      return JSON.parse(wrap.getAttribute('data-product-featured'));
    } catch (e) {
      return null;
    }
  }

  function handleFromUrl(url) {
    var match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  /** Datos mínimos reales tomados de la tarjeta, por si falla el fetch. */
  function fallbackFromCard(el) {
    var card = el.closest('.t4s-product') || el.closest('[data-product-options]');
    if (!card) return {};
    var link = card.querySelector('.t4s-product-title a, a[data-pr-href]');
    var img = card.querySelector('img');
    return {
      title: link ? link.textContent.trim() : '',
      url: link ? link.href : '',
      image: realSrc(img),
      handle: handleFromUrl(link ? link.getAttribute('href') : '')
    };
  }

  /* ---------------------------------------------------------
     Interceptación de "añadir"
     --------------------------------------------------------- */
  /**
   * El tema imprime dos campos name="id": un input oculto con la variante
   * inicial y un <select> que SÍ se mantiene sincronizado con los swatches.
   * El <select> manda cuando existe.
   */
  function variantIdFromForm(form, data) {
    var select = form.querySelector('select[name="id"]');
    if (select && select.value) return select.value;
    var hidden = form.querySelector('input[name="id"]');
    if (hidden && hidden.value) return hidden.value;
    return data.get('id');
  }

  function resolveFromForm(form) {
    var data = new FormData(form);
    var variantId = variantIdFromForm(form, data);
    var qty = data.get('quantity') || 1;

    var properties = {};
    data.forEach(function (value, name) {
      var match = name.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    });

    var featured = readFeaturedOptions(form);
    var handle =
      (featured && featured.productHandle) ||
      form.getAttribute('data-product-handle') ||
      handleFromUrl(window.location.pathname);

    return fetchProduct(handle).then(function (product) {
      var variant = null;
      if (product && product.variants) {
        variant =
          product.variants.filter(function (v) {
            return String(v.id) === String(variantId);
          })[0] || null;
      }
      if (!variant && !variantId) return null;
      return buildItem(product, variant, qty, properties, {
        variantId: variantId,
        handle: handle,
        title: document.title,
        image: domImageNear(form)
      });
    });
  }

  function resolveFromCard(el) {
    var options = readCardOptions(el);
    var fallback = fallbackFromCard(el);
    var handle = (options && options.handle) || fallback.handle;
    var variantId =
      el.getAttribute('data-variant-id') || (options && options.VariantFirstID) || null;

    var scope = el.closest('.t4s-product-atc-qty') || el.closest('.t4s-product');
    var qtyInput = scope ? scope.querySelector('[data-quantity-value]') : null;
    var qty = (qtyInput && qtyInput.value) || el.getAttribute('dat-qty') || 1;

    return fetchProduct(handle).then(function (product) {
      var variant = null;
      if (product && product.variants) {
        variant =
          product.variants.filter(function (v) {
            return String(v.id) === String(variantId);
          })[0] ||
          product.variants[0] ||
          null;
      }
      if (!variant && !variantId) return null;
      return buildItem(product, variant, qty, {}, {
        variantId: variantId,
        handle: handle,
        title: fallback.title,
        url: fallback.url,
        image: fallback.image
      });
    });
  }

  function acceptAdd(promise, sourceEl) {
    if (sourceEl) sourceEl.classList.add('is--pq-loading');
    promise
      .then(function (item) {
        if (sourceEl) sourceEl.classList.remove('is--pq-loading');
        if (!item || !item.variantId) {
          Drawer.open(S.errorGeneric || 'No pudimos añadir el producto. Intenta de nuevo.', true);
          return;
        }
        Store.add(item);
        Drawer.open(S.added || 'Producto añadido a tu cotización');
      })
      .catch(function () {
        if (sourceEl) sourceEl.classList.remove('is--pq-loading');
        Drawer.open(S.errorGeneric || 'No pudimos añadir el producto. Intenta de nuevo.', true);
      });
  }

  function onClickCapture(event) {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey) return;
    var target = event.target;
    if (!target || !target.closest) return;

    var trigger = target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    /* Productos realmente deshabilitados conservan su comportamiento nativo. */
    if (trigger.hasAttribute('disabled')) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    var form = trigger.closest(FORM_SELECTOR);
    acceptAdd(form ? resolveFromForm(form) : resolveFromCard(trigger), trigger);
  }

  function onSubmitCapture(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches(FORM_SELECTOR)) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    acceptAdd(resolveFromForm(form), form.querySelector('[data-atc-form]'));
  }

  /* ---------------------------------------------------------
     Renderizado de líneas
     --------------------------------------------------------- */
  function itemMetaHtml(item) {
    var rows = [];
    var variantText = item.variantLabel || item.variantTitle;
    if (variantText) rows.push(escapeHtml(variantText));
    if (item.sku) rows.push(escapeHtml((S.reference || 'Referencia') + ': ' + item.sku));
    (item.properties || []).forEach(function (p) {
      rows.push(escapeHtml(p.name + ': ' + p.value));
    });
    return rows
      .map(function (row) {
        return '<span>' + row + '</span>';
      })
      .join('');
  }

  function itemHtml(item) {
    var media = item.image
      ? '<img src="' +
        escapeHtml(item.image) +
        '" alt="' +
        escapeHtml(item.title) +
        '" width="72" height="72" loading="lazy">'
      : '';
    var price =
      item.price && item.price > 0
        ? '<div class="pq-item__price">' + escapeHtml(formatMoney(item.price)) + '</div>'
        : '';
    var title = item.url
      ? '<a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a>'
      : escapeHtml(item.title);

    return (
      '<li class="pq-item" data-pq-line="' + escapeHtml(item.key) + '">' +
      '<div class="pq-item__media">' + media + '</div>' +
      '<div class="pq-item__info">' +
      '<h3 class="pq-item__title">' + title + '</h3>' +
      '<p class="pq-item__meta">' + itemMetaHtml(item) + '</p>' +
      price +
      '</div>' +
      '<div class="pq-item__side">' +
      '<div class="pq-qty">' +
      '<button type="button" data-pq-dec aria-label="' +
      escapeHtml(S.decrease || 'Restar una unidad') + '"' +
      (item.qty <= 1 ? ' disabled' : '') + '>&minus;</button>' +
      '<input type="number" inputmode="numeric" min="1" max="9999" step="1" value="' +
      escapeHtml(item.qty) + '" data-pq-qty aria-label="' +
      escapeHtml((S.quantityFor || 'Cantidad para') + ' ' + item.title) + '">' +
      '<button type="button" data-pq-inc aria-label="' +
      escapeHtml(S.increase || 'Sumar una unidad') + '">+</button>' +
      '</div>' +
      '<button type="button" class="pq-remove" data-pq-remove>' +
      escapeHtml(S.remove || 'Eliminar') +
      '</button>' +
      '</div>' +
      '</li>'
    );
  }

  function emptyHtml(compact) {
    return (
      '<div class="pq-empty">' +
      '<svg class="pq-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M9 3h6l1 3H8l1-3z"></path><path d="M4 6h16l-1.2 13.2a2 2 0 0 1-2 1.8H7.2a2 2 0 0 1-2-1.8L4 6z"></path>' +
      '<path d="M9.5 11.5l5 5M14.5 11.5l-5 5"></path></svg>' +
      '<p class="pq-empty__title">' + escapeHtml(S.emptyTitle || 'Tu cotización está vacía') + '</p>' +
      '<p class="pq-empty__text">' +
      escapeHtml(S.emptyText || 'Agrega productos para comenzar tu solicitud.') +
      '</p>' +
      '<a class="pq-btn pq-btn--primary" href="' + escapeHtml(CFG.catalogUrl || rootUrl() + 'collections/all') + '">' +
      escapeHtml(S.explore || 'Explorar productos') +
      '</a>' +
      '</div>'
    );
  }

  /** Delegación compartida de cantidad / eliminar. */
  function bindLineActions(root) {
    if (!root || root.__pqBound) return;
    root.__pqBound = true;

    root.addEventListener('click', function (event) {
      var line = event.target.closest('[data-pq-line]');
      if (!line) return;
      var key = line.getAttribute('data-pq-line');
      var item = Store.find(key);
      if (!item) return;

      if (event.target.closest('[data-pq-remove]')) {
        Store.remove(key);
      } else if (event.target.closest('[data-pq-inc]')) {
        Store.setQty(key, (parseInt(item.qty, 10) || 1) + 1);
      } else if (event.target.closest('[data-pq-dec]')) {
        Store.setQty(key, (parseInt(item.qty, 10) || 1) - 1);
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target.closest('[data-pq-qty]');
      if (!input) return;
      var line = input.closest('[data-pq-line]');
      if (!line) return;
      Store.setQty(line.getAttribute('data-pq-line'), input.value);
    });
  }

  /* ---------------------------------------------------------
     Drawer (mini cotización)
     --------------------------------------------------------- */
  var Drawer = {
    el: null,
    listEl: null,
    flashEl: null,
    lastFocus: null,

    init: function () {
      this.el = document.getElementById('pq-drawer');
      if (!this.el) return;
      this.listEl = this.el.querySelector('[data-pq-drawer-list]');
      this.flashEl = this.el.querySelector('[data-pq-flash]');

      var self = this;
      qsa('[data-pq-close]', this.el).forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.close();
        });
      });
      var overlay = document.getElementById('pq-overlay');
      if (overlay) {
        overlay.addEventListener('click', function () {
          self.close();
        });
      }
      document.addEventListener('keydown', function (event) {
        if (!document.documentElement.classList.contains('pq-is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          self.close();
        } else if (event.key === 'Tab') {
          self.trapFocus(event);
        }
      });

      bindLineActions(this.listEl);
      this.render();
    },

    trapFocus: function (event) {
      var focusables = qsa(
        'a[href], button:not([disabled]), input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])',
        this.el
      ).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    render: function () {
      if (!this.listEl) return;
      var items = Store.read();
      this.listEl.innerHTML = items.length
        ? '<ul class="pq-list">' + items.map(itemHtml).join('') + '</ul>'
        : emptyHtml(true);
      var foot = this.el.querySelector('[data-pq-drawer-foot]');
      if (foot) foot.hidden = items.length === 0;
    },

    open: function (message, isError) {
      if (!this.el) {
        /* Sin drawer en la página: al menos confirmamos y actualizamos contadores. */
        announce(message);
        return;
      }
      this.lastFocus = document.activeElement;
      this.render();

      if (this.flashEl) {
        if (message) {
          this.flashEl.querySelector('[data-pq-flash-text]').textContent = message;
          this.flashEl.hidden = false;
          this.flashEl.classList.toggle('pq-flash--error', !!isError);
        } else {
          this.flashEl.hidden = true;
        }
      }
      announce(message);

      document.documentElement.classList.add('pq-is-open', 'pq-locked');
      document.body.classList.add('pq-locked');
      this.el.removeAttribute('aria-hidden');

      var focusTarget = this.el.querySelector('[data-pq-close]');
      if (focusTarget) window.setTimeout(function () { focusTarget.focus(); }, 60);
    },

    close: function () {
      if (!this.el) return;
      document.documentElement.classList.remove('pq-is-open', 'pq-locked');
      document.body.classList.remove('pq-locked');
      this.el.setAttribute('aria-hidden', 'true');
      if (this.flashEl) this.flashEl.hidden = true;
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
  };

  function announce(message) {
    if (!message) return;
    var region = document.getElementById('pq-live');
    if (region) {
      region.textContent = '';
      window.setTimeout(function () { region.textContent = message; }, 30);
    }
  }

  /* ---------------------------------------------------------
     Acceso "Mi cotización" en el header
     Se monta dentro de los grupos de iconos nativos del tema
     (.t4s-site-nav__icons), de modo que hereda su posición y
     sus colores sin tocar los snippets del tema.
     --------------------------------------------------------- */
  var TRIGGER_MARKUP =
    '<a class="pq-trigger" href="__URL__">' +
    '<svg class="pq-trigger__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M9 3h6a1 1 0 0 1 1 1v1h2.5A1.5 1.5 0 0 1 20 6.5v13A1.5 1.5 0 0 1 18.5 21h-13A1.5 1.5 0 0 1 4 19.5v-13A1.5 1.5 0 0 1 5.5 5H8V4a1 1 0 0 1 1-1z"></path>' +
    '<path d="M8.5 12.5h7M8.5 16h4.5"></path></svg>' +
    '<span class="pq-trigger__label">__LABEL__</span>' +
    '<span class="pq-trigger__count" data-prenco-quote-count hidden>0</span>' +
    '</a>';

  function mountTriggers() {
    var label = S.myQuote || 'Mi cotización';
    var url = CFG.quoteUrl || rootUrl() + 'pages/mi-cotizacion';
    var groups = qsa('.t4s-site-nav__icons');

    if (!groups.length) {
      var wrapper = document.querySelector('.t4s-header__wrapper');
      if (wrapper) groups = [wrapper];
    }

    groups.forEach(function (group) {
      if (group.querySelector('.pq-trigger')) return;
      var holder = document.createElement('div');
      holder.className = 't4s-site-nav__icon t4s-site-nav__quote';
      holder.innerHTML = TRIGGER_MARKUP.replace('__URL__', escapeHtml(url)).replace(
        '__LABEL__',
        escapeHtml(label)
      );
      group.appendChild(holder);
    });
  }

  /* ---------------------------------------------------------
     Contador del header
     Cuenta productos (líneas), no unidades: una obra de 250
     bloques no debe mostrar "250" en el badge.
     --------------------------------------------------------- */
  function refreshCounters() {
    var count = Store.lines();
    qsa('[data-prenco-quote-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
      var trigger = el.closest('.pq-trigger');
      if (trigger) {
        trigger.setAttribute(
          'aria-label',
          (S.myQuote || 'Mi cotización') + (count ? ' (' + count + ')' : '')
        );
        trigger.classList.add('is--bumped');
        window.setTimeout(function () { trigger.classList.remove('is--bumped'); }, 260);
      }
    });
  }

  /* ---------------------------------------------------------
     Mensaje de WhatsApp
     --------------------------------------------------------- */
  function buildWhatsappMessage(contact) {
    var items = Store.read();
    var lines = [];

    lines.push('Hola, PRENCO.');
    lines.push('Quiero solicitar una cotización para los siguientes productos:');
    lines.push('');

    items.forEach(function (item, index) {
      lines.push(index + 1 + '. ' + item.title);
      if (item.sku) lines.push('Referencia: ' + item.sku);
      var variantText = item.variantTitle || item.variantLabel;
      if (variantText) lines.push('Variante: ' + variantText);
      (item.properties || []).forEach(function (p) {
        lines.push(p.name + ': ' + p.value);
      });
      lines.push('Cantidad: ' + item.qty);
      lines.push('');
    });

    lines.push('Datos de contacto:');
    if (contact.name) lines.push('Nombre: ' + contact.name);
    if (contact.company) lines.push('Empresa: ' + contact.company);
    if (contact.phone) lines.push('Teléfono: ' + contact.phone);
    if (contact.city) lines.push('Ciudad: ' + contact.city);
    if (contact.comments) {
      lines.push('Comentarios:');
      lines.push(contact.comments);
    }
    lines.push('');
    lines.push('Quedo atento a la cotización.');

    return lines.join('\n');
  }

  function whatsappUrl(contact) {
    return 'https://wa.me/' + WHATSAPP + '?text=' + encodeURIComponent(buildWhatsappMessage(contact));
  }

  /* ---------------------------------------------------------
     Página "Mi cotización"
     --------------------------------------------------------- */
  var Page = {
    root: null,
    listEl: null,
    formEl: null,

    init: function () {
      this.root = document.querySelector('[data-prenco-quote-page]');
      if (!this.root) return;
      this.listEl = this.root.querySelector('[data-pq-page-list]');
      this.formEl = this.root.querySelector('[data-pq-form]');

      bindLineActions(this.listEl);
      this.restoreContact();

      var self = this;
      var submit = this.root.querySelector('[data-pq-submit]');
      if (submit) {
        submit.addEventListener('click', function (event) {
          event.preventDefault();
          self.submit();
        });
      }
      if (this.formEl) {
        this.formEl.addEventListener('submit', function (event) {
          event.preventDefault();
          self.submit();
        });
        this.formEl.addEventListener('input', function (event) {
          var field = event.target;
          if (field.getAttribute('aria-invalid') === 'true' && field.value.trim()) {
            self.setFieldError(field, '');
          }
        });
      }
      this.render();
    },

    field: function (name) {
      return this.formEl ? this.formEl.querySelector('[name="' + name + '"]') : null;
    },

    contact: function () {
      var self = this;
      var get = function (name) {
        var el = self.field(name);
        return el ? el.value.trim() : '';
      };
      return {
        name: get('name'),
        company: get('company'),
        phone: get('phone'),
        city: get('city'),
        comments: get('comments')
      };
    },

    restoreContact: function () {
      var saved = Contact.read();
      var self = this;
      Object.keys(saved).forEach(function (key) {
        var el = self.field(key);
        if (el && !el.value) el.value = saved[key];
      });
    },

    setFieldError: function (field, message) {
      if (!field) return;
      var wrap = field.closest('.pq-field');
      var error = wrap ? wrap.querySelector('.pq-field__error') : null;
      if (message) {
        field.setAttribute('aria-invalid', 'true');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
      } else {
        field.removeAttribute('aria-invalid');
        if (error) error.hidden = true;
      }
    },

    validate: function () {
      var ok = true;
      var first = null;
      var self = this;
      [
        { name: 'name', message: S.errorName || 'Escribe tu nombre.' },
        { name: 'phone', message: S.errorPhone || 'Escribe un teléfono de contacto.' }
      ].forEach(function (rule) {
        var field = self.field(rule.name);
        if (!field) return;
        if (!field.value.trim()) {
          self.setFieldError(field, rule.message);
          ok = false;
          if (!first) first = field;
        } else {
          self.setFieldError(field, '');
        }
      });
      if (first) first.focus();
      return ok;
    },

    submit: function () {
      if (!Store.lines()) {
        announce(S.emptyTitle || 'Tu cotización está vacía');
        return;
      }
      if (!this.validate()) {
        announce(S.errorForm || 'Revisa los campos obligatorios.');
        return;
      }
      var contact = this.contact();
      Contact.write(contact);
      window.open(whatsappUrl(contact), '_blank', 'noopener');
    },

    /**
     * Contexto de producto. Si el usuario llega desde una ficha con
     * ?producto=<handle>, ofrecemos añadirlo mientras no esté ya en la
     * cotización. Con varias variantes enlazamos a la ficha en lugar de
     * elegir una por él.
     */
    context: function () {
      var slot = this.root.querySelector('[data-pq-context]');
      if (!slot) return;

      var handle = '';
      try {
        handle = new URLSearchParams(window.location.search).get('producto') || '';
      } catch (e) {
        handle = '';
      }
      if (!handle) {
        slot.innerHTML = '';
        return;
      }

      fetchProduct(handle).then(function (product) {
        var exists = Store.read().some(function (i) {
          return i.handle === handle;
        });
        if (!product || exists) {
          slot.innerHTML = '';
          return;
        }

        var variants = product.variants || [];
        var single = variants.length === 1;
        var variant = variants[0];
        var image = sizedImage(productImage(product, variant, ''), 120);
        var url = rootUrl() + 'products/' + product.handle;

        slot.innerHTML =
          '<div class="pq-context">' +
          (image
            ? '<div class="pq-context__media"><img src="' + escapeHtml(image) +
              '" alt="" width="44" height="44"></div>'
            : '') +
          '<div class="pq-context__info">' +
          '<span class="pq-context__eyebrow">' +
          escapeHtml(S.contextLabel || 'Producto consultado') +
          '</span>' +
          '<span class="pq-context__title">' + escapeHtml(product.title) + '</span>' +
          '</div>' +
          (single
            ? '<button type="button" class="pq-btn pq-btn--primary pq-context__action" data-pq-context-add>' +
              escapeHtml(S.contextAdd || 'Añadir a la cotización') +
              '</button>'
            : '<a class="pq-btn pq-btn--ghost pq-context__action" href="' + escapeHtml(url) + '">' +
              escapeHtml(S.contextChoose || 'Elegir opciones') +
              '</a>') +
          '</div>';

        var addBtn = slot.querySelector('[data-pq-context-add]');
        if (addBtn) {
          addBtn.addEventListener('click', function () {
            Store.add(buildItem(product, variant, 1, {}, {}));
          });
        }
      });
    },

    render: function () {
      if (!this.listEl) return;
      var items = Store.read();
      this.listEl.innerHTML = items.length
        ? '<ul class="pq-list">' + items.map(itemHtml).join('') + '</ul>'
        : emptyHtml(false);

      this.context();

      var aside = this.root.querySelector('[data-pq-aside]');
      if (aside) aside.hidden = items.length === 0;

      var counter = this.root.querySelector('[data-pq-page-count]');
      if (counter) {
        var n = Store.lines();
        counter.textContent =
          n === 1
            ? '1 ' + (S.unitOne || 'producto')
            : n + ' ' + (S.unitMany || 'productos');
      }
    }
  };

  /* ---------------------------------------------------------
     Arranque
     --------------------------------------------------------- */
  function boot() {
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('submit', onSubmitCapture, true);

    mountTriggers();
    Drawer.init();
    Page.init();
    refreshCounters();

    document.addEventListener('prenco:quote:updated', function () {
      refreshCounters();
      Drawer.render();
      Page.render();
    });

    /* Sincroniza entre pestañas. */
    window.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      Store._items = null;
      refreshCounters();
      Drawer.render();
      Page.render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* API pública mínima, útil para depuración y para el editor de temas. */
  window.PrencoQuote = {
    store: Store,
    open: function () { Drawer.open(); },
    close: function () { Drawer.close(); },
    message: buildWhatsappMessage,
    url: whatsappUrl
  };
})();
